import React, { useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, MenuItem, TextField } from '@mui/material';

import useAuth from '../hooks/useAuth';
import {
  getAnnouncements,
  markAnnouncementRead,
  markAnnouncementUnread,
} from '../apis/repositories/announcementsRepository';
import type { Announcement } from 'types/domain';

import styles from './Announcements.module.css';

const Announcements = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'read'>('all');

  const unreadCount = useMemo(
    () => announcements.filter((announcement) => !announcement.isRead).length,
    [announcements]
  );

  const filteredAnnouncements = useMemo(() => {
    if (activeFilter === 'unread') {
      return announcements.filter((announcement) => !announcement.isRead);
    }

    if (activeFilter === 'read') {
      return announcements.filter((announcement) => announcement.isRead);
    }

    return announcements;
  }, [announcements, activeFilter]);

  const loadAnnouncements = async () => {
    setLoading(true);
    setError('');

    try {
      const list = await getAnnouncements(user?.uid);
      setAnnouncements(list);
    } catch (apiError) {
      console.error(apiError);
      setError('Unable to fetch announcements right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, [user?.uid]);

  const handleToggleRead = async (announcement: Announcement) => {
    if (!user?.uid) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (announcement.isRead) {
        await markAnnouncementUnread(user.uid, announcement.id);
      } else {
        await markAnnouncementRead(user.uid, announcement.id);
      }
      await loadAnnouncements();
    } catch (apiError) {
      console.error(apiError);
      setError('Unable to update read status right now.');
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.heading}>Announcements</div>
      <div className={styles.subHeading}>Unread: {unreadCount}</div>

      <div className={styles.filterRow}>
        <TextField
          select
          label="View"
          size="small"
          value={activeFilter}
          onChange={(event) => setActiveFilter(event.target.value as 'all' | 'unread' | 'read')}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="unread">Unread</MenuItem>
          <MenuItem value="read">Read</MenuItem>
        </TextField>
      </div>

      {loading && announcements.length === 0 ? (
        <div className={styles.loader}>
          <CircularProgress size={24} />
        </div>
      ) : null}

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.list}>
        {filteredAnnouncements.length === 0 && !loading ? (
          <div className={styles.emptyState}>No announcements in this view.</div>
        ) : null}
        {filteredAnnouncements.map((announcement) => (
          <div key={announcement.id} className={styles.announcementCard} data-read={announcement.isRead}>
            <div className={styles.announcementTitle}>{announcement.title}</div>
            <div className={styles.announcementMessage}>{announcement.message}</div>
            <div className={styles.meta}>
              {announcement.createdAt ? `Posted ${new Date(announcement.createdAt).toLocaleString()}` : ''}
              {announcement.expiresAt ? ` • Expires ${new Date(announcement.expiresAt).toLocaleString()}` : ''}
            </div>
            {announcement.createdBy ? (
              <div className={styles.meta}>Posted by {announcement.createdBy}</div>
            ) : null}
            <div className={styles.actionRow}>
              <Button size="small" onClick={() => handleToggleRead(announcement)} disabled={loading || !user?.uid}>
                {announcement.isRead ? 'Mark Unread' : 'Mark Read'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Announcements;
